import React from 'react';
import {
  Dialog,
  DialogContent,
  Grid2,
  Button,
  Typography,
  DialogActions,
  Divider,
} from '@mui/material';
import { createRoot } from 'react-dom/client';
import strings from 'constants/strings';
import { useWindowDimensions } from 'hooks/useWindowDimensions';

export const errorTextMapping = strings.error;
export interface IError {
  key: string;
  msg: string;
  statusCode: number;
}

export const ErrorContent: React.FC<{
  title: string;
  children?: React.ReactNode;
}> = (props) => {
  const titles = props.title.split('\n');
  return (
    <div className={'flex flexAlignCenter flexColumn'}>
      {titles.map((title, index) => (
        <h3 key={index} className="textAlignCenter">
          {title}
        </h3>
      ))}
      {props.children}
    </div>
  );
};

export const PopupDialogContent: React.FC<{
  message: string;
  detail: string;
}> = (props) => {
  const { message, detail } = props;
  const messages = message.split('\n');
  const details = detail.split('\n');
  return (
    <div className={'flex flexAlignCenter flexColumn'}>
      {messages.map((msg, index) => (
        <h3 key={index} className="textAlignCenter">
          {msg}
        </h3>
      ))}
      {details.map((det, index) => (
        <p
          key={index}
          className="textAlignCenter"
          style={{ marginTop: '16px', marginBottom: 0 }}
        >
          {det}
        </p>
      ))}
    </div>
  );
};

export type DialogType = 'confirmation' | 'legacy' | 'custom' | 'error';

export type DialogActionType =
  | 'positive'
  | 'neutral'
  | 'negative'
  | 'destructive';

export type DialogAction = string | { label: string; action?: () => void };

export interface PopupDialogConfig {
  message: string;
  detail: string;
  type?: DialogType;
  actions?: {
    [key in DialogActionType]?: DialogAction;
  };
}

const PopupDialogComponent: React.FC<{
  open: boolean;
  config: PopupDialogConfig;
  onClose: () => void;
}> = ({ open, config, onClose }) => {
  const { message, detail, type = 'confirmation', actions } = config;
  const { infoDialogSize, isMobile } = useWindowDimensions();
  const messages = message.split('\n').filter((msg) => msg.trim() !== '');
  const details = detail.split('\n').filter((det) => det.trim() !== '');

  const getActionLabel = (action: DialogAction): string => {
    return typeof action === 'string' ? action : action.label;
  };

  const performAction = (actionType: DialogActionType) => {
    const action = actions?.[actionType];
    if (action && typeof action === 'object' && action.action) {
      action.action();
    }
    onClose();
  };

  const handleClose = (
    _event: object,
    reason?: 'backdropClick' | 'escapeKeyDown',
  ) => {
    if (reason === 'backdropClick') {
      return;
    }
    onClose();
  };

  const renderButtons = () => {
    // Use new actions system if available
    if (actions && Object.keys(actions).length > 0) {
      // Get action buttons in order: positive, destructive, negative
      const actionButtonInfos = [
        actions.positive
          ? {
              type: 'positive' as DialogActionType,
              label: getActionLabel(actions.positive),
              onClick: () => performAction('positive'),
              colorClass: 'fontColor--green',
            }
          : null,
        actions.destructive
          ? {
              type: 'destructive' as DialogActionType,
              label: getActionLabel(actions.destructive),
              onClick: () => performAction('destructive'),
              colorClass: 'fontColor--red',
            }
          : null,
        actions.negative
          ? {
              type: 'negative' as DialogActionType,
              label: getActionLabel(actions.negative),
              onClick: () => performAction('negative'),
              colorClass: 'fontColor--grey',
            }
          : null,
      ].filter((info) => info !== null) as Array<{
        type: DialogActionType;
        label: string;
        onClick: () => void;
        colorClass: string;
      }>;

      const middleButtonInfo = actions.neutral
        ? {
            type: 'neutral' as DialogActionType,
            label: getActionLabel(actions.neutral),
            onClick: () => performAction('neutral'),
            colorClass: 'fontColor--grey',
          }
        : null;

      const rightButtonInfo = actionButtonInfos[0] || null;
      const leftButtonInfo = actionButtonInfos[1] || null;

      const buttonCount =
        (leftButtonInfo ? 1 : 0) +
        (middleButtonInfo ? 1 : 0) +
        (rightButtonInfo ? 1 : 0);

      if (buttonCount === 0) {
        return (
          <Button
            onClick={onClose}
            variant="text"
            className="widthPercent--100 p24 fontColor--grey"
          >
            閉じる
          </Button>
        );
      }

      const getWidthClass = (position: 'left' | 'middle' | 'right') => {
        if (buttonCount === 1) return 'widthPercent--100';
        if (buttonCount === 2) {
          return position === 'left'
            ? 'widthPercent--50 mr0'
            : 'widthPercent--50 ml0';
        }
        if (buttonCount === 3) {
          return position === 'left'
            ? 'widthPercent--33 mr0'
            : position === 'right'
              ? 'widthPercent--33 ml0'
              : 'widthPercent--33';
        }
        return '';
      };

      return (
        <>
          {leftButtonInfo && (
            <>
              <Button
                onClick={leftButtonInfo.onClick}
                variant="text"
                className={`${getWidthClass('left')} p16 ${leftButtonInfo.colorClass}`}
              >
                {leftButtonInfo.label}
              </Button>
              {(middleButtonInfo || rightButtonInfo) && (
                <Divider orientation="vertical" flexItem className="ml0" />
              )}
            </>
          )}
          {middleButtonInfo && (
            <>
              <Button
                onClick={middleButtonInfo.onClick}
                variant="text"
                className={`${getWidthClass('middle')} p16 ${middleButtonInfo.colorClass}`}
              >
                {middleButtonInfo.label}
              </Button>
              {rightButtonInfo && (
                <Divider orientation="vertical" flexItem className="ml0" />
              )}
            </>
          )}
          {rightButtonInfo && (
            <Button
              onClick={rightButtonInfo.onClick}
              variant="text"
              className={`${getWidthClass('right')} p16 ${rightButtonInfo.colorClass}`}
            >
              {rightButtonInfo.label}
            </Button>
          )}
        </>
      );
    }

    // No actions provided, show close button
    return (
      <Button
        onClick={onClose}
        variant="text"
        className="widthPercent--100 p24 fontColor--grey"
      >
        閉じる
      </Button>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: isMobile ? '16px' : '24px',
          padding: 0,
          width: infoDialogSize.width,
        },
      }}
    >
      <DialogContent
        sx={{
          padding: '24px 32px 24px 32px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Grid2
          container
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          width="100%"
        >
          {messages.map((msg, index) => (
            <Typography
              key={index}
              className={`textAlignCenter fontWeight--bold fontColor--grey fontSize--16 ${type === 'error' ? 'fontColor--red' : 'fontColor--grey'}`}
              sx={{
                marginBottom: index < messages.length - 1 ? '8px' : 0,
                marginTop: index === 0 ? '8px' : 0,
              }}
            >
              {msg}
            </Typography>
          ))}
          {details.map((det, index) => (
            <Typography
              key={index}
              className="textAlignCenter fontColor--grey fontSize--14"
              sx={{
                marginTop: index === 0 ? '16px' : '8px',
                marginBottom: index < details.length - 1 ? '8px' : 0,
              }}
            >
              {det}
            </Typography>
          ))}
        </Grid2>
      </DialogContent>
      <DialogActions className="flexCenterCenter p0 borderTop--lightGrey">
        {renderButtons()}
      </DialogActions>
    </Dialog>
  );
};

export interface confirmDialogProps {
  title: string;
  titleClassName?: string;
  okText?: string;
  cancelText?: string;
  okButtonClassName?: string;
  cancelButtonClassName?: string;
}

export const errorObject = {
  invalid_password: 'invalid_password',
  toke_expired_or_not_valid_now: 'toke_expired_or_not_valid_now',
  temppermission_time_conflict: 'temppermission_time_conflict',
  time_conflict: 'time_conflict',
  invalid_patient_email: 'invalid_patient_email',
  admin_no_already_exists: 'admin_no_already_exists',
  duplicate_email: 'duplicate_email',
  email_already_exists: 'email_already_exists',
};

export const showInfoDialog = (
  message: string,
  onOk?: () => void,
  detail?: string,
) => {
  showPopupDialog(message, detail || '', {
    actions: {
      neutral: {
        label: 'OK',
        action: onOk,
      },
    },
  });
};

export const showPopupDialog = (
  message: string,
  detail: string,
  config?: {
    type?: DialogType;
    actions?: {
      [key in DialogActionType]?: DialogAction;
    };
  },
) => {
  const dialogConfig: PopupDialogConfig = {
    message,
    detail,
    ...config,
  };

  // Create a container for the dialog
  const container = document.createElement('div');
  document.body.appendChild(container);
  const root = createRoot(container);

  const handleClose = () => {
    root.unmount();
    document.body.removeChild(container);
  };

  root.render(
    <PopupDialogComponent
      open={true}
      config={dialogConfig}
      onClose={handleClose}
    />,
  );
};
export const getErrorKey = (err: IError | any) => {
  const textKey = errorObject[err.key as keyof typeof errorObject];
  if (textKey) {
    return textKey;
  }
  const key = err?.response?.data?.key;
  if (key) {
    return key;
  }
  return undefined;
};

export const handleErrorToast = (err: IError | any, errorText?: string) => {
  const textKey = getErrorKey(err);
  const message =
    errorTextMapping[textKey as keyof typeof errorTextMapping] ||
    errorText ||
    strings.general.label.server_error;
  showInfoDialog(message);
};

export const handleErrorToastWithCode = (errorCode?: string) => {
  const message = `ただいま通信が混み合っている可能性がございます。\nコード：${errorCode}`;
  showPopupDialog('', message, {
    actions: {
      neutral: {
        label: '閉じる',
        action: () => {},
      },
    },
  });
};
